import { useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useUserAccess } from '../context/UserAccessContext';
import { useSantri } from '../context/SantriContext';
import { useGenderKelas } from '../hooks/useGenderKelas';
import { useGuru } from '../context/GuruContext';
import { useMasterData } from '../context/MasterDataContext';

/**
 * DataInitializer — invisible component yang mengevaluasi role user
 * dan mentrigger fetch data sesuai menu yang diakses.
 * 
 * Dipindahkan dari PermitContext.initializeData() (logic non-perizinan).
 * Dipasang di App.tsx setelah provider hierarchy selesai.
 */
const DataInitializer: React.FC = () => {
    const { authUser } = useAuth();
    const { isSuperAdmin, userPermissions } = useUserAccess();
    const { fetchSantriCustomClasses } = useSantri();
    const { checkIsKelasPutra, checkIsKelasPutri } = useGenderKelas();
    const { guruRecords, loading: guruLoading } = useGuru();
    const { kelasRecords, loading: masterLoading } = useMasterData();

    useEffect(() => {
        if (!authUser || userPermissions.length === 0 || guruLoading || masterLoading) return;

        const initializeNonPermitData = async () => {
            try {
                const permData = userPermissions.find(p => p.email === authUser?.email);
                const currentUserPerms = permData?.allowedMenus || [];

                const globalSantriMenus = [
                    'pg-departemen-pendidikan', 'pg-waka-kesiswaan', 'pg-kesantrian',
                    'pg-kepala-sekolah', 'pg-kepala-divisi', 'pg-waka-kurikulum', 'pg-koordinator-tahfidz',
                    'pg-mudir', 'pg-bagian-kesehatan',
                    'pg-sekretariat', 'pg-bagian-perizinan', 'pg-bagian-resepsionis', 'pg-tata-usaha'
                ];

                const teacherSantriMenus = [
                    'pg-akademik', 'pg-tahfidz', 'pg-murobbi', 'pg-wali-kelas'
                ];

                const needsGlobalSantriData = isSuperAdmin || currentUserPerms.some((menu: string) => globalSantriMenus.includes(menu));
                const needsTeacherSantriData = !needsGlobalSantriData && currentUserPerms.some((menu: string) => teacherSantriMenus.includes(menu));
                const needsKegiatanSantriData = !needsGlobalSantriData && currentUserPerms.some((menu: string) => ['pg-menu-presensi-kegiatan', 'pg-kiosk-presensi', 'pg-kelola-kegiatan'].includes(menu));
                const needsStafGenderData = !needsGlobalSantriData && currentUserPerms.some((menu: string) => ['pg-staf-tarbiyah', 'pg-staf-lughoh'].includes(menu));

                const needsHeavyMasterData = needsGlobalSantriData || needsTeacherSantriData || needsKegiatanSantriData || needsStafGenderData;

                if (!needsHeavyMasterData) return;

                let targetClasses: string[] | null = null;
                let kegiatanClasses: string[] = [];
                let stafGenderClasses: string[] = [];
                let customSantriIds: string[] = [];
                const classSet = new Set<string>();

                if (needsKegiatanSantriData || needsStafGenderData) {
                    const allKelasNames = kelasRecords.map(k => k.namaKelas).filter(Boolean) as string[];

                    if (needsKegiatanSantriData) {
                        kegiatanClasses = allKelasNames;
                    }

                    if (needsStafGenderData) {
                        try {
                            const me = guruRecords.find(g => (g.email?.toLowerCase().trim() || '') === (authUser?.email?.toLowerCase().trim() || ''));
                            if (me) {
                                const guruGender = me.jenisKelamin;
                                if (guruGender === 'Laki-laki' || guruGender === 'L') {
                                    stafGenderClasses = allKelasNames.filter(k => checkIsKelasPutra(k));
                                } else if (guruGender === 'Perempuan' || guruGender === 'P') {
                                    stafGenderClasses = allKelasNames.filter(k => checkIsKelasPutri(k));
                                }
                            }
                        } catch (err) {
                            console.error("Error evaluating staff gender", err);
                        }
                    }
                }

                if (needsTeacherSantriData || needsStafGenderData || needsKegiatanSantriData) {
                    try {
                        if (needsTeacherSantriData) {
                            const me = guruRecords.find(g => (g.email?.toLowerCase().trim() || '') === (authUser?.email?.toLowerCase().trim() || ''));
                            if (me) {
                                const guruName = me.nama;

                                const [jadwalSnap, halaqohSnap, murobbiSnap] = await Promise.all([
                                    getDocs(query(collection(db, 'jadwal_pelajaran'), where('guru', '==', guruName))),
                                    getDocs(query(collection(db, 'halaqoh'), where('pengampu', '==', guruName))),
                                    getDocs(query(collection(db, 'murobbi'), where('nama', '==', guruName)))
                                ]);

                                // Filter kelas dari MasterDataContext langsung
                                const kelasWali = kelasRecords.filter(k => k.waliKelas === guruName);

                                jadwalSnap.docs.forEach(doc => { const cls = doc.data().kelas; if (cls) classSet.add(cls); });
                                murobbiSnap.docs.forEach(doc => { const cls = doc.data().kelas; if (cls) classSet.add(cls); });
                                kelasWali.forEach(k => { const cls = k.namaKelas; if (cls) classSet.add(cls); });

                                halaqohSnap.docs.forEach(doc => {
                                    const data = doc.data();
                                    if (data.santri && Array.isArray(data.santri)) {
                                        data.santri.forEach((s: any) => { if (s.id) customSantriIds.push(s.id); });
                                    }
                                });
                            }
                        }

                        targetClasses = Array.from(new Set([...kegiatanClasses, ...stafGenderClasses, ...Array.from(classSet)]));

                        if (targetClasses.length === 0 && customSantriIds.length > 0) {
                            targetClasses = null;
                        } else if (targetClasses.length === 0 && customSantriIds.length === 0) {
                            targetClasses = [];
                        } else if (targetClasses.length > 0 && customSantriIds.length > 0) {
                            targetClasses = null;
                        }
                    } catch (err) {
                        console.error("Error evaluating combined teacher classes", err);
                        targetClasses = [];
                    }
                }

                await fetchSantriCustomClasses(targetClasses);
            } catch (e) {
                console.error("DataInitializer error", e);
            }
        };

        initializeNonPermitData();
    }, [authUser, isSuperAdmin, userPermissions, guruLoading, masterLoading, guruRecords, kelasRecords]);

    return null;
};

export default DataInitializer;
